<template>
  <div v-if="loading || (suggestions && suggestions.length > 0)">
    <div class="tw-font-bold tw-mb-1">{{ $t('Suggestions') }}</div>
    <WdsSpinner v-if="loading" indeterminate class="tw-text-indigo-400" />
    <list-animation v-else>
      <WdsLabel
        v-for="suggestion in suggestions"
        :key="suggestion"
        class="tw-cursor-pointer tw-mr-1 tw-mb-1 tw-opacity-80 hover:tw-opacity-100"
        subtle
        primary
        @click="() => handleAddSuggestion(suggestion)"
      >
        {{ suggestion }}
      </WdsLabel>
    </list-animation>
    <WdsText secondary small class="tw-mt-1">{{ $t('Click to add') }}</WdsText>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, Ref } from 'vue'
import { WdsLabel, WdsText, WdsSpinner } from '@worksome/wds-components'
import ListAnimation from '@/components/ListAnimation.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = withDefaults(
  defineProps<{
    /** Skill names to exclude from suggestion results */
    excludedSkillNames: string[]
    /** Skills used to fetch related suggestions */
    skillsForSuggestions?: string[]
    /** Maximum number of suggestions to display */
    maxSuggestions?: number
  }>(),
  {
    excludedSkillNames: () => [],
    skillsForSuggestions: () => [],
    maxSuggestions: 15,
  },
)

const suggestions: Ref<string[]> = ref([])
const loading = ref(false)

watch(
  () => props.skillsForSuggestions,
  async () => {
    if (props.skillsForSuggestions.length) {
      try {
        loading.value = true
        const response = await fetch(
          `/relatedskills?q=${encodeURIComponent(props.skillsForSuggestions.join(','))}`,
        )
        const data: string[] = await response.json()
        suggestions.value = Object.values(data)
          .filter((skill) => !props.excludedSkillNames.includes(skill))
          .slice(0, props.maxSuggestions)
      } catch (error) {
        suggestions.value = []
      } finally {
        loading.value = false
      }
    }
  },
)

const emit = defineEmits(['add-suggestion'])

const handleAddSuggestion = (addedSuggestion: string) => {
  suggestions.value = suggestions.value.filter((suggestion) => suggestion !== addedSuggestion)
  emit('add-suggestion', addedSuggestion)
}
</script>

<style scoped lang="scss">
.fadeIn {
  @apply tw-opacity-100;
  @apply tw-animate-pulse;
}

.fadeOut {
  @apply tw-opacity-0;
  @apply tw-animate-pulse;
}
</style>
